import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import { fetchAPI } from '../../utils/api';
import { showSuccess, showError } from '../../utils/toastUtils';
import { useConfirm } from '../../hooks/useConfirm';
// import ConfirmDialog from '../../components/common/ConfirmDialog';

export default function AdminTeachers() {
    const { confirm, ConfirmDialog: confirmDialogProps } = useConfirm();
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingTeacher, setEditingTeacher] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        subject: '',
        qualifications: '',
        phone: '',
        image_url: '',
        banner_url: ''
    });

    const loadTeachers = async () => {
        try {
            const data = await fetchAPI('/admin/get_teachers.php');
            setTeachers(data);
        } catch (err) {
            console.error("Failed to load teachers", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTeachers();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = editingTeacher ? { ...formData, id: editingTeacher.id } : formData;
            await fetchAPI('/admin/save_teacher.php', {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            showSuccess(editingTeacher ? 'Teacher updated' : 'Teacher added');
            setShowForm(false);
            setEditingTeacher(null);
            setFormData({ name: '', subject: '', qualifications: '', phone: '', image_url: '', banner_url: '' });
            loadTeachers();
        } catch (err) {
            showError("Error: " + err.message);
        }
    };

    const handleEdit = (teacher) => {
        setEditingTeacher(teacher);
        setFormData({
            name: teacher.name,
            subject: teacher.subject,
            qualifications: teacher.qualifications,
            phone: teacher.phone,
            image_url: teacher.image_url,
            banner_url: teacher.banner_url
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        const confirmed = await confirm({
            title: 'Delete Teacher',
            message: 'Are you sure you want to delete this teacher? This will delete all their classes as well.',
            confirmText: 'Delete Teacher',
            cancelText: 'Cancel',
            danger: true,
            icon: 'trash'
        });

        if (!confirmed) return;

        try {
            await fetchAPI('/admin/delete_teacher.php', {
                method: 'POST',
                body: JSON.stringify({ id })
            });
            showSuccess('Teacher deleted successfully.');
            loadTeachers();
        } catch (err) {
            showError("Error: " + err.message);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500 font-bold">Loading teachers...</div>;

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white/60 backdrop-blur-xl rounded-xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight font-display mb-2">
                        Teacher Management
                    </h1>
                    <p className="text-gray-500 font-medium">
                        Manage teaching staff and their profiles.
                    </p>
                </div>
                <button
                    onClick={() => { setShowForm(true); setEditingTeacher(null); setFormData({ name: '', subject: '', qualifications: '', phone: '', image_url: '', banner_url: '' }); }}
                    className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-lg shadow-blue-200"
                >
                    <FaPlus /> Add Teacher
                </button>
            </div>

            {showForm && (
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 animated fadeIn">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-bold text-gray-900">{editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}</h3>
                        <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                    </div>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Full Name</label>
                                <input name="name" value={formData.name} onChange={handleChange} className="w-full border rounded-xl p-3 bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none" required />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Subject</label>
                                <input name="subject" value={formData.subject} onChange={handleChange} className="w-full border rounded-xl p-3 bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none" required />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Qualifications</label>
                                <textarea name="qualifications" value={formData.qualifications} onChange={handleChange} className="w-full border rounded-xl p-3 bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none" rows="3" required />
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Phone</label>
                                <input name="phone" value={formData.phone} onChange={handleChange} className="w-full border rounded-xl p-3 bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none" required />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Image URL</label>
                                <input name="image_url" value={formData.image_url} onChange={handleChange} className="w-full border rounded-xl p-3 bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Banner URL</label>
                                <input name="banner_url" value={formData.banner_url} onChange={handleChange} className="w-full border rounded-xl p-3 bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none" />
                            </div>
                        </div>
                        <div className="md:col-span-2 flex justify-end gap-3 mt-4 border-t pt-6">
                            <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-xl transition-all">Cancel</button>
                            <button type="submit" className="px-8 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-100 flex items-center gap-2">
                                <FaSave /> {editingTeacher ? 'Update Teacher' : 'Save Teacher'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {teachers.map((t) => (
                    <div key={t.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden group hover:shadow-md transition-all">
                        <div className="h-32 bg-gray-100 relative">
                            {t.banner_url && <img src={t.banner_url} className="w-full h-full object-cover" alt="Banner" />}
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex justify-end p-2 gap-2">
                                <button onClick={() => handleEdit(t)} className="w-8 h-8 bg-white text-blue-600 rounded-full flex items-center justify-center shadow-lg"><FaEdit size={12} /></button>
                                <button onClick={() => handleDelete(t.id)} className="w-8 h-8 bg-white text-red-600 rounded-full flex items-center justify-center shadow-lg"><FaTrash size={12} /></button>
                            </div>
                        </div>
                        <div className="p-6 -mt-10 relative text-center">
                            <img src={t.image_url || 'https://via.placeholder.com/150'} className="w-20 h-20 rounded-full border-4 border-white mx-auto shadow-md object-cover bg-white" alt={t.name} />
                            <h3 className="text-lg font-bold text-gray-900 mt-4">{t.name}</h3>
                            <p className="text-blue-600 text-xs font-black uppercase tracking-widest mt-1">{t.subject}</p>
                            <div className="text-sm text-gray-500 mt-4 font-medium italic line-clamp-2">
                                {t.qualifications}
                            </div>
                            <div className="mt-6 flex justify-center gap-4 text-xs font-bold text-gray-400">
                                <span>{t.phone}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
